import { Account } from "@rms-types/modules/account/entities/account";
import { PermissionEnum } from "@rms-types/modules/permission/enum";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { flatten, intersectionBy, uniq } from "lodash";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { accountEndpoint } from "@/constants/api/account";

export interface UserStore {
  account: Account | null;
  permissionTypes: PermissionEnum[];
  hasPermission: (permissionToCheck: PermissionEnum | PermissionEnum[]) => boolean;
  clearStore(): void;
  updateUser(account: Account): void;
  loadUser(): Promise<void>;
}

const initialState: Pick<UserStore, "account" | "permissionTypes"> = {
  account: null,
  permissionTypes: [],
};

export const useUserStore = create<UserStore, [["zustand/persist", UserStore]]>(
  persist(
    (set, get) => ({
      ...initialState,
      hasPermission: (permissionToCheck) => {
        const { permissionTypes } = get();
        // An admin user can see everything
        if (permissionTypes.includes(PermissionEnum.ADMIN_ACCESS)) {
          return true;
        }

        if (Array.isArray(permissionToCheck)) {
          return intersectionBy(permissionTypes, permissionToCheck).length > 0;
        }

        return permissionTypes.includes(permissionToCheck);
      },
      clearStore: () => set(initialState),
      updateUser: (account: Account) => set((prev) => ({ ...prev, account })),
      loadUser: async () => {
        try {
          const { data: account } = await axios.get<Account>(accountEndpoint.checkUser);
          set({
            account,
            permissionTypes: account
              ? uniq(
                  flatten(
                    account.account_roles?.map(({ role }) =>
                      role.role_permissions.map(({ permission }) => permission.type)
                    )
                  )
                )
              : [],
          });
        } catch (error) {
          Sentry.captureException(error);
        }
      },
    }),
    {
      name: "user-store",
      version: 1,
    }
  )
);
